import PlusIcon from "assets/icons/HeroIcons/PlusIcon";
import TrashIcon from "assets/icons/HeroIcons/TrashIcon";
import { FieldArray, FormikProps } from "formik";
import React from "react";
import { Step } from "store/addReportStepper/initialState";
import { ISummaryReports } from "store/reportsSummary";
import { IReportSettingsFormValues } from "../..";
import { MaterialUiSelect } from "../Select";
import { TailSpin } from "react-loader-spinner";
import useCustomBranding from "hooks/useCustomBranding";
import GpsCoordinatesFilterInputs from "./GpsCoordinatesFilterInputs";
import useAvailableColumns from "./useAvailableColumns";
import ColumnsDropdown from "./ColumnsDropdown";
import ConditionDropdown from "./ConditionDropdown";
import { renderComponentByType } from "./renderers";
import { IColumn } from "./types";
import { COORDINATE_FORMATS } from "hooks/useGpsCoordinates";

interface ReportSettingsProps extends FormikProps<IReportSettingsFormValues> {
  activeStep: any;
  isLastStep: any;
  steps: Step[];
  reportId: any;
  reportSummary: ISummaryReports;
  fetchReportsSummary: (id: number) => void;
  moveToPreviousStep: () => void;
  moveToNextStep: () => void;
  setAnythingUpdatedThisStep: (currentStep: string, isUpdated: boolean) => void;
}

export const OtherQueryBuilder: React.FC<ReportSettingsProps> = ({
  activeStep,
  isLastStep,
  steps,
  reportId,
  reportSummary,
  fetchReportsSummary,
  moveToPreviousStep,
  moveToNextStep,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  setFieldError,
  setFieldTouched,
}) => {
  const { availableColumns, isColumnsLoading } = useAvailableColumns(
    reportSummary?.id,
  );
  const { primaryColor } = useCustomBranding();

  const filteredColumns = availableColumns.filter(
    (item: IColumn) =>
      !values.otherQueryFilter.some((columnItem: any) => {
        return columnItem.column === item.column;
      }),
  );

  if (isColumnsLoading) {
    return (
      <div className="flex items-center justify-center w-full loader">
        <TailSpin
          height="50"
          width="50"
          color={primaryColor}
          ariaLabel="tail-spin-loading"
          radius="2"
          wrapperClass="tailspin-loader"
          visible={true}
        />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-start self-stretch justify-start w-full gap-4 bg-white rounded-lg">
      <FieldArray name="otherQueryFilter">
        {({ push, remove }) => (
          <div className="w-full">
            {values.otherQueryFilter.map((rowKey, index) => {
              const otherQueryFilterErrors: any = errors?.otherQueryFilter
                ? errors.otherQueryFilter[index]
                : {
                    column: null,
                    condition: null,
                    operator: null,
                    type: null,
                    value: null,
                  };

              const otherQueryFilterTouched: any = touched?.otherQueryFilter?.[
                index
              ] || {
                column: false,
                condition: false,
                operator: false,
                type: false,
                value: false,
              };

              const type = values?.otherQueryFilter[index]?.type;

              const operatorSelector =
                index > 0 ? (
                  <div className="w-[100px] flex-shrink-0 py-3">
                    <MaterialUiSelect
                      options={[
                        { label: "and", value: "and" },
                        { label: "or", value: "or" },
                      ]}
                      labelKey="label"
                      valueKey="value"
                      error={otherQueryFilterErrors?.operator}
                      initialValue={values?.otherQueryFilter[index]?.operator}
                      handleChange={(value: any) => {
                        setFieldValue(
                          `otherQueryFilter[${index}].operator`,
                          value.value,
                        );
                      }}
                      width={100}
                      className="border-none captitalize"
                      styles={{ textTransform: "capitalize" }}
                    />
                  </div>
                ) : (
                  <div className="w-[100px] dark:text-textMain mt-4 flex-shrink-0">
                    <p className="mt-[5px] text-center uppercase">Where</p>
                  </div>
                );

              const columnSelector = (
                <div className="flex-1 max-w-[25%] py-3">
                  <ColumnsDropdown
                    rowIndex={index}
                    values={values}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    otherQueryFilterErrors={otherQueryFilterErrors}
                    otherQueryFilterTouched={otherQueryFilterTouched}
                    availableColumns={availableColumns}
                  />
                </div>
              );

              const conditionSelector = (
                <div className="flex-1 py-3">
                  <ConditionDropdown
                    rowIndex={index}
                    values={values}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    otherQueryFilterErrors={otherQueryFilterErrors}
                    otherQueryFilterTouched={otherQueryFilterTouched}
                    reportSummary={reportSummary}
                  />
                </div>
              );

              const renderValue = () => {
                if (type === "gpsCoordinates") {
                  const currentValue =
                    values?.otherQueryFilter?.[index]?.value || {};
                  return (
                    <GpsCoordinatesFilterInputs
                      index={index}
                      value={currentValue}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      otherQueryFilterErrors={otherQueryFilterErrors}
                      otherQueryFilterTouched={otherQueryFilterTouched}
                      handleBlur={handleBlur}
                    />
                  );
                }

                return renderComponentByType(
                  index,
                  values,
                  setFieldValue,
                  otherQueryFilterErrors,
                  otherQueryFilterTouched,
                  handleChange,
                  handleBlur,
                  setFieldTouched,
                  availableColumns,
                );
              };

              const isSimpleLayout = ["radiogroup", "checkbox"].includes(type);

              if (["gpsCoordinates", "matrixdynamic"].includes(type)) {
                return (
                  <div className="flex flex-col w-full gap-2" key={index}>
                    <div className="flex items-start w-full gap-3">
                      {operatorSelector}
                      {columnSelector}
                      {conditionSelector}
                      <div className="w-[50px] cursor-pointer mt-3">
                        <TrashIcon onClick={() => remove(index)} />
                      </div>
                    </div>
                    <div className="w-full">{renderValue()}</div>
                  </div>
                );
              }

              if (["text", "number", "dropdown", "tagbox"].includes(type)) {
                return (
                  <div className="flex items-start w-full gap-3" key={index}>
                    {operatorSelector}
                    {columnSelector}
                    {conditionSelector}
                    <div className="flex-2 max-w-[60%] items-start justify-start w-full">
                      {renderValue()}
                    </div>
                    <div className="w-[50px] cursor-pointer mt-3">
                      <TrashIcon onClick={() => remove(index)} />
                    </div>
                  </div>
                );
              }

              if (isSimpleLayout) {
                return (
                  <div className="flex items-start w-full gap-2" key={index}>
                    {operatorSelector}
                    {columnSelector}
                    {conditionSelector}
                    <div className="w-[50px] cursor-pointer mt-3">
                      <TrashIcon onClick={() => remove(index)} />
                    </div>
                    <div className="flex-1 max-w-[25%] items-start justify-start w-full">
                      {renderValue()}
                    </div>
                  </div>
                );
              }

              return null;
            })}

            <button
              disabled={filteredColumns.length === 0}
              type="button"
              title={`${
                filteredColumns.length === 0 && "No More Columns To Add"
              }`}
              onClick={() => {
                if (filteredColumns.length === 0) return;
                const firstColumn = filteredColumns[0];
                const defaultValue =
                  firstColumn.type === "gpsCoordinates"
                    ? {
                        coordinates_format: COORDINATE_FORMATS.DECIMAL_DEGREES,
                        latitude: "",
                        longitude: "",
                      }
                    : firstColumn.type === "matrixdynamic"
                      ? []
                      : "";

                push({
                  column: firstColumn.column,
                  columnId: firstColumn.columnId,
                  condition: "==",
                  operator: "and",
                  type: firstColumn.type,
                  inputType: firstColumn.inputType,
                  metaInfo: firstColumn.metaInfo,
                  value: defaultValue,
                });
              }}
              className={`flex items-center mb-6 disabled:cursor-not-allowed mt-2 ${filteredColumns.length === 0 ? "opacity-[0.5]" : ""}`}
            >
              <PlusIcon fill={primaryColor} />
              <p className="ml-2 text-sm font-semibold text-textLink ">
                Add Another
              </p>
            </button>
          </div>
        )}
      </FieldArray>
    </div>
  );
};

export default OtherQueryBuilder;
